/**
 * Callix Dialer Widget - Entry Point para CDN
 * 
 * ⚠️ SEGURANÇA:
 * - Este widget NÃO contém credenciais sensíveis (API_KEY, DOMAIN)
 * - Todas as credenciais ficam no backend (API: /api/user-sdk-session)
 * - Cliente envia apenas USERNAME → Backend valida e retorna TOKEN
 * - Token é temporário e gerenciado pelo backend
 * 
 * 📦 USO VIA CDN:
 * ```html
 * <script src="https://cdn.jsdelivr.net/npm/@callixbrasil/dialer-widget/dist/callix-dialer.umd.js"></script>
 * <script>
 *   CallixDialer.init({
 *     container: '#dialer',
 *     apiUrl: 'https://seu-backend.com/api/user-sdk-session'
 *   });
 * </script>
 * ```
 */

import { createRoot, type Root } from 'react-dom/client';
import { FloatingDialer } from '../components/FloatingDialer';
import { InlineDialer } from '../components/InlineDialer';
import { StrictMode, useState, useEffect, useRef, useCallback, useMemo, createContext, useContext, forwardRef, useImperativeHandle } from 'react';
import { CallixClientProvider, useCallOperatorControls, useCallOperator } from '@callixbrasil/client-sdk-react';

// ============================================================================
// WEBSOCKET SIP CLEANUP - Fecha conexões SIP órfãs ANTES de iniciar
// Isso é CRÍTICO para resolver o erro SIP 409 em reload de página
// ============================================================================

// Armazena referência ao WebSocket SIP atual para cleanup
let currentSipWebSocket: WebSocket | null = null;

/**
 * Tenta enviar uma mensagem SIP UNREGISTER antes de fechar a conexão
 * Isso pode ajudar o Kamailio a limpar o registro imediatamente
 */
function sendSipUnregisterIfPossible(ws: WebSocket): void {
  if (ws.readyState !== WebSocket.OPEN) return;
  
  try {
    // Mensagem SIP mínima de UNREGISTER (pode não funcionar dependendo do servidor)
    // O importante é tentar fechar gracefully
    console.log('[Callix Dialer] 📤 Tentando enviar sinalização de fechamento...');
    
    // A biblioteca jssip armazena informações no WebSocket
    // Vamos tentar acessar para obter o Call-ID
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wsAny = ws as Record<string, any>;
    
    // Verificar se há método de cleanup disponível
    if (wsAny._ua && typeof wsAny._ua.stop === 'function') {
      console.log('[Callix Dialer] 🔌 Encontrado UA, chamando stop()...');
      wsAny._ua.stop();
    } else if (wsAny._connection && typeof wsAny._connection.close === 'function') {
      console.log('[Callix Dialer] 🔌 Fechando conexão interna...');
      wsAny._connection.close();
    }
  } catch (err) {
    console.warn('[Callix Dialer] ⚠️ Não foi possível enviar UNREGISTER:', err);
  }
}

/**
 * Intercepta criação de WebSockets para capturar conexões SIP
 * Isso permite fechar a conexão SIP ao recarregar a página
 */
function setupWebSocketInterceptor() {
  if (typeof window === 'undefined') return;
  
  // Evitar setup duplicado
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).__callixWsInterceptorSetup) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__callixWsInterceptorSetup = true;
  
  const OriginalWebSocket = window.WebSocket;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).WebSocket = function(url: string, protocols?: string | string[]) {
    const ws = new OriginalWebSocket(url, protocols);
    
    // Detectar se é uma conexão SIP do Callix (wss://...callix.com.br/ws)
    if (typeof url === 'string' && url.includes('callix') && url.includes('/ws')) {
      console.log('[Callix Dialer] 🔌 WebSocket SIP detectado:', url);
      currentSipWebSocket = ws;
      
      // Capturar mensagens SIP para debug
      ws.addEventListener('message', (event) => {
        if (typeof event.data === 'string' && event.data.includes('Call-ID:')) {
          const match = event.data.match(/Call-ID:\s*([^\r\n]+)/i);
          if (match) {
            // Log para debug, não precisamos armazenar
            match[1].trim();
          }
        }
      });
      
      // Limpar referência quando fechar
      ws.addEventListener('close', () => {
        console.log('[Callix Dialer] 🔌 WebSocket SIP fechado');
        if (currentSipWebSocket === ws) {
          currentSipWebSocket = null;
        }
      });
    }
    
    return ws;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;
  
  // Preservar propriedades estáticas do WebSocket
  Object.assign(window.WebSocket, {
    CONNECTING: OriginalWebSocket.CONNECTING,
    OPEN: OriginalWebSocket.OPEN,
    CLOSING: OriginalWebSocket.CLOSING,
    CLOSED: OriginalWebSocket.CLOSED,
  });
  
  console.log('[Callix Dialer] ✅ WebSocket interceptor configurado');
}

/**
 * Fecha qualquer WebSocket SIP ativo
 * Deve ser chamado ANTES de iniciar nova autenticação
 */
function closeExistingSipWebSocket(): Promise<void> {
  return new Promise((resolve) => {
    if (!currentSipWebSocket) {
      console.log('[Callix Dialer] 📭 Nenhum WebSocket SIP ativo para fechar');
      resolve();
      return;
    }
    
    if (currentSipWebSocket.readyState === WebSocket.CLOSED || 
        currentSipWebSocket.readyState === WebSocket.CLOSING) {
      console.log('[Callix Dialer] 📭 WebSocket SIP já está fechando/fechado');
      currentSipWebSocket = null;
      resolve();
      return;
    }
    
    console.log('[Callix Dialer] 🔌 Fechando WebSocket SIP existente...');
    
    const ws = currentSipWebSocket;
    
    // Tentar enviar sinalização de cleanup antes de fechar
    sendSipUnregisterIfPossible(ws);
    
    const timeoutId = setTimeout(() => {
      console.log('[Callix Dialer] ⏱️ Timeout ao fechar WebSocket, continuando...');
      // Forçar fechamento
      try {
        ws.close(1000, 'Force close after timeout');
      } catch {
        // Ignorar erro
      }
      resolve();
    }, 3000); // Aumentar timeout para dar tempo ao UNREGISTER
    
    ws.addEventListener('close', () => {
      clearTimeout(timeoutId);
      console.log('[Callix Dialer] ✅ WebSocket SIP fechado com sucesso');
      resolve();
    }, { once: true });
    
    // Aguardar um pouco para a sinalização ser enviada antes de fechar
    setTimeout(() => {
      // Enviar código 1000 (normal closure)
      ws.close(1000, 'Page reload - cleaning up SIP connection');
    }, 500);
  });
}

/**
 * Cleanup completo ao sair da página
 * Registra handler de beforeunload para fechar WebSocket SIP
 */
function setupPageUnloadCleanup() {
  if (typeof window === 'undefined') return;
  
  // Evitar setup duplicado
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).__callixUnloadSetup) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__callixUnloadSetup = true;
  
  window.addEventListener('beforeunload', () => {
    console.log('[Callix Dialer] 🚪 Página fechando, limpando WebSocket SIP...');
    if (currentSipWebSocket && currentSipWebSocket.readyState === WebSocket.OPEN) {
      // Fechar imediatamente - não há tempo para esperar
      currentSipWebSocket.close(1000, 'Page unload');
    }
  });
  
  // Também no pagehide (mais confiável em mobile)
  window.addEventListener('pagehide', () => {
    if (currentSipWebSocket && currentSipWebSocket.readyState === WebSocket.OPEN) {
      currentSipWebSocket.close(1000, 'Page hide');
    }
  });
  
  console.log('[Callix Dialer] ✅ Page unload cleanup configurado');
}

// Inicializar interceptors imediatamente
setupWebSocketInterceptor();
setupPageUnloadCleanup();

/**
 * Adiciona meta tag para desabilitar detecção automática de formato no iOS Safari
 * Previne que números/textos sejam detectados como telefone, endereço, etc.
 */
function setupIOSFormatDetection() {
  if (typeof document === 'undefined') return;
  
  // Verificar se já existe a meta tag
  const existingMeta = document.querySelector('meta[name="format-detection"]');
  if (existingMeta) return;
  
  // Criar e adicionar a meta tag
  const meta = document.createElement('meta');
  meta.name = 'format-detection';
  meta.content = 'telephone=no, date=no, email=no, address=no';
  document.head.appendChild(meta);
  
  console.log('[Callix Dialer] ✅ iOS format-detection meta tag adicionada');
}

// Configurar meta tag para iOS
setupIOSFormatDetection();

// ============================================================================
// CONTEXT E COMPONENTES
// ============================================================================

// Context para compartilhar skipManualQualification
type DialerConfigContextValue = {
  skipManualQualification: boolean;
};

const DialerConfigContext = createContext<DialerConfigContextValue | null>(null);

export function useDialerConfig(): DialerConfigContextValue {
  const context = useContext(DialerConfigContext);
  if (!context) {
    // Valor padrão se usado fora do provider
    return { skipManualQualification: false };
  }
  return context;
}

// ============================================================================
// SDK CLEANUP MANAGER
// Este componente é CRÍTICO para resolver o erro SIP 409
// Ele permite chamar goOffline() e stop() ANTES do CallixClientProvider desmontar
// ============================================================================

interface SdkCleanupHandle {
  /** Chama goOffline() no CallOperator para enviar SIP UNREGISTER */
  goOffline: () => void;
  /** Chama stop() no CallOperator para parar completamente */
  stop: () => void;
}

/**
 * Componente interno que expõe métodos de cleanup do SDK via ref
 * Deve ser renderizado DENTRO do CallixClientProvider
 */
const SdkCleanupManager = forwardRef<SdkCleanupHandle, { children: React.ReactNode }>(
  function SdkCleanupManager({ children }, ref) {
    const operator = useCallOperator();
    const controls = useCallOperatorControls();

    // Expor métodos de cleanup via ref
    useImperativeHandle(ref, () => ({
      goOffline: () => {
        console.log('[Callix Dialer] 📴 Chamando goOffline() para enviar SIP UNREGISTER...');
        try {
          controls.goOffline();
          console.log('[Callix Dialer] ✅ goOffline() chamado com sucesso');
        } catch (err) {
          console.error('[Callix Dialer] ❌ Erro ao chamar goOffline():', err);
        }
      },
      stop: () => {
        console.log('[Callix Dialer] 🛑 Chamando stop() no CallOperator...');
        try {
          operator.stop();
          console.log('[Callix Dialer] ✅ stop() chamado com sucesso');
        } catch (err) {
          console.error('[Callix Dialer] ❌ Erro ao chamar stop():', err);
        }
      },
    }), [operator, controls]);

    // Cleanup automático no unmount - chamar goOffline antes de desmontar
    useEffect(() => {
      return () => {
        console.log('[Callix Dialer] 🔌 SdkCleanupManager desmontando, chamando goOffline()...');
        try {
          controls.goOffline();
        } catch (err) {
          console.warn('[Callix Dialer] ⚠️ Erro no cleanup automático:', err);
        }
      };
    }, [controls]);

    return <>{children}</>;
  }
);

// Tipos para as opções de inicialização
function deriveOptionsFromElement(element: HTMLElement): Partial<Pick<CallixDialerOptions, 'uiVariant' | 'presetUsername'>> {
  const result: Partial<Pick<CallixDialerOptions, 'uiVariant' | 'presetUsername'>> = {};
  const datasetMode = element.getAttribute('data-callix-mode') || element.dataset.callixMode || element.dataset.mode;
  const datasetUsername = element.getAttribute('data-callix-username') || element.dataset.callixUsername || element.dataset.username;
  const classList = Array.from(element.classList);
  const lowerClasses = classList.map((cls) => cls.toLowerCase());
  const hasCallixClass = lowerClasses.includes('callix');
  const normalizedMode = datasetMode ? datasetMode.toLowerCase() : undefined;

  if (normalizedMode === 'min' || normalizedMode === 'mini') {
    result.uiVariant = 'min';
  } else if (hasCallixClass) {
    if (lowerClasses.includes('mode-min') || lowerClasses.includes('mode-mini') || (lowerClasses.includes('mode') && lowerClasses.includes('min'))) {
      result.uiVariant = 'min';
    }
  }

  if (!result.presetUsername && datasetUsername) {
    result.presetUsername = datasetUsername.trim();
  }

  if (!result.presetUsername) {
    for (const className of classList) {
      const match = className.match(/(?:user|username|callix-user)[-_:]([A-Za-z0-9.@_-]+)/i);
      if (match) {
        result.presetUsername = match[1];
        break;
      }
    }
  }

  if (result.presetUsername) {
    result.presetUsername = result.presetUsername.trim();
  }

  return result;
}

interface CallixDialerOptions {
  container: string | HTMLElement;
  apiUrl?: string;
  domain?: string;
  mode?: 'floating' | 'inline'; // Novo: modo de exibição
  theme?: 'light' | 'dark';
  position?: 'bottom-right' | 'bottom-left';
  onReady?: () => void;
  onError?: (error: Error) => void;
  uiVariant?: 'default' | 'min' | 'mobile';
  presetUsername?: string;
  portalTarget?: HTMLElement | null;
}

// Classe principal do Widget (similar a uma classe Python exportável)
class CallixDialerWidget {
  private root: Root | null = null;
  private container: HTMLElement | null = null;
  private options: CallixDialerOptions;

  constructor(options: CallixDialerOptions) {
    this.options = options;
  }

  /**
   * Inicializa o widget no DOM
   * Similar a: widget = MinhaClasse(); widget.init()
   */
  init(): void {
    try {
      // Encontrar ou criar container
      if (typeof this.options.container === 'string') {
        this.container = document.querySelector(this.options.container);
      } else {
        this.container = this.options.container;
      }

      if (!this.container) {
        throw new Error(`Container não encontrado: ${this.options.container}`);
      }

      const derived = deriveOptionsFromElement(this.container);
      if (!this.options.uiVariant && derived.uiVariant) {
        this.options.uiVariant = derived.uiVariant;
      }
      if (!this.options.presetUsername && derived.presetUsername) {
        this.options.presetUsername = derived.presetUsername;
      }

      // Criar root do React
      this.root = createRoot(this.container);

      // Renderizar componente
      this.root.render(
        <StrictMode>
          <DialerWrapper options={this.options} containerEl={this.container} />
        </StrictMode>
      );

      // Callback de sucesso
      if (this.options.onReady) {
        this.options.onReady();
      }
    } catch (error) {
      if (this.options.onError) {
        this.options.onError(error as Error);
      } else {
        console.error('[Callix Dialer] Erro ao inicializar:', error);
      }
    }
  }

  /**
   * Destrói o widget e limpa recursos
   */
  destroy(): void {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

// Chaves para localStorage - DINÂMICAS POR USUÁRIO
const getStorageKeys = (username: string) => ({
  SESSION_TOKEN: `callix_dialer_${username}_session_token`,
  DOMAIN: `callix_dialer_${username}_domain`,
  TIMESTAMP: `callix_dialer_${username}_timestamp`,
  SKIP_MANUAL_QUALIFICATION: `callix_dialer_${username}_skip_manual_qualification`,
});

// Chave global para último usuário logado
const LAST_USERNAME_KEY = 'callix_dialer_last_username';

// Componente wrapper interno
function DialerWrapper({ options, containerEl }: { options: CallixDialerOptions; containerEl?: HTMLElement | null }) {
  const [username, setUsername] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [callixDomain, setCallixDomain] = useState<string | null>(null);
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [skipManualQualification, setSkipManualQualification] = useState<boolean>(false);
  
  // Ref para o SdkCleanupManager - permite chamar goOffline/stop antes do unmount
  const cleanupRef = useRef<SdkCleanupHandle>(null);
  // Key única para forçar recriação do CallixClientProvider
  // Isso garante que a conexão SIP seja completamente destruída e recriada
  const [providerKey, setProviderKey] = useState<number>(() => Date.now());
  
  // Flag para controlar quando o SDK está "desconectando"
  // Isso cria um delay entre desmontar o provider e remontar
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const presetUsername = options.presetUsername ? options.presetUsername.trim() : null;
  const uiVariant = options.uiVariant ?? 'default';
  const portalTarget = useMemo(() => options.portalTarget ?? containerEl ?? null, [options.portalTarget, containerEl]);
  const autoLoginAttemptedRef = useRef(false);
  const lastPresetRef = useRef<string | null>(presetUsername);

  // Ref para armazenar apiUrl e token atual para uso no beforeunload
  const currentSessionRef = useRef<{ apiUrl: string; token: string | null }>({ 
    apiUrl: options.apiUrl || '/api/user-sdk-session', 
    token: null 
  });

  // Atualizar ref quando token mudar
  useEffect(() => {
    currentSessionRef.current.token = sessionToken;
  }, [sessionToken]);

  // Fazer logout quando a página for fechada (beforeunload)
  // Isso ajuda a liberar o registro SIP no servidor
  useEffect(() => {
    const handleBeforeUnload = () => {
      const { apiUrl, token } = currentSessionRef.current;
      if (token) {
        // Usar sendBeacon para garantir que a requisição seja enviada mesmo ao fechar
        const data = JSON.stringify({ logout: true });
        const logoutUrl = `${apiUrl}?logout=true`;
        
        // Tentar com sendBeacon (mais confiável ao fechar página)
        if (navigator.sendBeacon) {
          const blob = new Blob([data], { type: 'application/json' });
          navigator.sendBeacon(logoutUrl, blob);
          console.log('[Callix Dialer] Logout enviado via sendBeacon');
        } else {
          // Fallback: fetch síncrono (não recomendado, mas melhor que nada)
          fetch(apiUrl, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
            keepalive: true,
          }).catch(() => {});
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // NÃO restaurar sessão do localStorage - sempre fazer novo login
  // Isso resolve o problema de SIP 409 (conflito de registro)
  // O servidor SIP requer uma nova sessão a cada pageload para evitar conflitos
  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsRestoringSession(false);
      return;
    }

    try {
      // Buscar o último username usado para auto-login
      const storedLastUsername = localStorage.getItem(LAST_USERNAME_KEY);

      if (!storedLastUsername) {
        console.log('[Callix Dialer] Nenhum username salvo, aguardando login manual');
        setIsRestoringSession(false);
        return;
      }

      if (presetUsername && storedLastUsername !== presetUsername) {
        console.log('[Callix Dialer] Ignorando sessao salva por mismatch com preset:', storedLastUsername, '!=', presetUsername);
        setIsRestoringSession(false);
        return;
      }

      // Apenas restaurar o username para facilitar auto-login
      // MAS NÃO restaurar o token - vamos buscar um novo do backend
      console.log('[Callix Dialer] Username encontrado, irá fazer novo login:', storedLastUsername);
      setUsername(storedLastUsername);
      
      // Restaurar apenas skipManualQualification (configuração local)
      const STORAGE_KEYS = getStorageKeys(storedLastUsername);
      const storedSkipQualification = localStorage.getItem(STORAGE_KEYS.SKIP_MANUAL_QUALIFICATION);
      if (storedSkipQualification) {
        setSkipManualQualification(storedSkipQualification === 'true');
      }

      // Não definir isAuthenticated = true
      // O auto-login será feito pelo useEffect de presetUsername
    } catch (err) {
      console.error('[Callix Dialer] Erro ao verificar sessão:', err);
    } finally {
      setIsRestoringSession(false);
    }
  }, [presetUsername]);

  useEffect(() => {
    if (presetUsername && username !== presetUsername) {
      setUsername(presetUsername);
    }
  }, [presetUsername, username]);

  const handleLogin = useCallback(async (user: string) => {
    setIsLoading(true);
    setError(null);

    console.log('[Callix Dialer] 🚀 handleLogin iniciado para usuário:', user);

    // ==========================================
    // PASSO 1: LOGOUT NO SERVIDOR - CRÍTICO PARA SIP 409
    // ==========================================
    // O registro SIP fantasma está no servidor Kamailio, NÃO no browser
    // Precisamos chamar a API para fazer logout ANTES de criar nova sessão
    
    const apiUrl = options.apiUrl || '/api/user-sdk-session';
    
    // Tentar recuperar token anterior do localStorage
    let previousToken: string | undefined;
    if (typeof window !== 'undefined') {
      try {
        const STORAGE_KEYS = getStorageKeys(user);
        previousToken = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN) || undefined;
        console.log('[Callix Dialer] 🔍 Token anterior no localStorage:', previousToken ? 'ENCONTRADO' : 'NÃO ENCONTRADO');
      } catch (err) {
        console.warn('[Callix Dialer] Erro ao buscar token anterior:', err);
      }
    }

    // Se existe token anterior, fazer logout NO SERVIDOR primeiro
    if (previousToken) {
      console.log('[Callix Dialer] 🔌 Token anterior encontrado, fazendo logout no servidor...');
      try {
        const logoutResponse = await fetch(apiUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${previousToken}`,
            'x-session-token': previousToken,
          },
          credentials: 'same-origin',
        });
        console.log('[Callix Dialer] 📡 Resposta do logout:', logoutResponse.status);
        
        if (logoutResponse.ok) {
          console.log('[Callix Dialer] ✅ Logout no servidor concluído');
        } else {
          console.log('[Callix Dialer] ⚠️ Logout retornou status:', logoutResponse.status);
        }
      } catch (logoutErr) {
        console.warn('[Callix Dialer] ⚠️ Erro no logout (pode ser sessão expirada):', logoutErr);
      }

      // AGUARDAR o Kamailio processar o unregister SIP
      // Este delay é CRÍTICO - o Kamailio precisa de tempo para limpar o registro
      console.log('[Callix Dialer] ⏳ Aguardando Kamailio processar unregister (5 segundos)...');
      await new Promise(resolve => setTimeout(resolve, 5000));
      console.log('[Callix Dialer] ✅ Período de espera concluído');
    }

    // Limpar localStorage
    if (typeof window !== 'undefined') {
      try {
        const STORAGE_KEYS = getStorageKeys(user);
        localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
        console.log('[Callix Dialer] 🗑️ Token antigo removido do localStorage');
      } catch (err) {
        console.warn('[Callix Dialer] Erro ao limpar localStorage:', err);
      }
    }

    // PASSO 2: Fechar qualquer WebSocket SIP órfão do browser (se existir)
    console.log('[Callix Dialer] 🔌 Verificando WebSocket SIP local...');
    await closeExistingSipWebSocket();

    // PASSO 3: Se já está autenticado (re-login), desmontar o provider primeiro
    if (isAuthenticated || sessionToken) {
      console.log('[Callix Dialer] 🔌 Preparando para fechar conexão SIP local...');
      
      // Chamar goOffline() via ref do SdkCleanupManager
      if (cleanupRef.current) {
        console.log('[Callix Dialer] 📴 Chamando goOffline() via cleanupRef...');
        cleanupRef.current.goOffline();
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('[Callix Dialer] 🛑 Chamando stop() via cleanupRef...');
        cleanupRef.current.stop();
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Desmontar o provider
      console.log('[Callix Dialer] ⏳ Desmontando CallixClientProvider...');
      setIsAuthenticated(false);
      setSessionToken(null);
      setIsDisconnecting(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsDisconnecting(false);
      console.log('[Callix Dialer] ✅ SDK desmontado');
    }

    // PASSO 4: Criar nova sessão no servidor
    try {
      console.log('[Callix Dialer] 🔐 Criando nova sessão no servidor...');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: user, previousToken }),
        credentials: 'same-origin', // Importante para cookies
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `Falha na autenticação: ${response.status}`);
      }

      const data = await response.json();
      
      // Armazenar token, domain e username
      setSessionToken(data.userSessionToken);
      setCallixDomain(data.domain || 'villelabrasil.callix.com.br');
      setUsername(user);
      setIsAuthenticated(true);
      setError(null);
      
      // Gerar nova key para forçar recriação do CallixClientProvider
      // Isso garante que seja uma instância completamente nova do SDK
      setProviderKey(Date.now());
      console.log('[Callix Dialer] Nova providerKey gerada para forçar recriação do SDK');

      // Buscar configuração do serviço (skipQualification para chamadas manuais)
      try {
        const domain = data.domain || 'villelabrasil.callix.com.br';
        const configUrl = `https://${domain}/sdk/v1/client/user-service/config`;
        console.log('[Callix Dialer] 🔍 Buscando configuração do serviço:', configUrl);

        const configResponse = await fetch(configUrl, {
          headers: {
            'Authorization': `Bearer ${data.userSessionToken}`,
          },
          credentials: 'omit',
        });

        console.log('[Callix Dialer] 📡 Resposta da config:', configResponse.status);

        if (configResponse.ok) {
          const configData = await configResponse.json();
          console.log('[Callix Dialer] 📦 Dados da config recebidos:', JSON.stringify(configData, null, 2));

          const shouldSkipQualification = configData?.service?.outboundOptions?.skipQualification === true;
          console.log('[Callix Dialer] ✅ skipQualification configurado:', shouldSkipQualification);
          setSkipManualQualification(shouldSkipQualification);

          // Salvar no localStorage
          if (typeof window !== 'undefined') {
            const STORAGE_KEYS = getStorageKeys(user);
            localStorage.setItem(STORAGE_KEYS.SKIP_MANUAL_QUALIFICATION, shouldSkipQualification.toString());
            console.log('[Callix Dialer] 💾 skipQualification salvo no localStorage');
          }
        } else {
          const errorText = await configResponse.text();
          console.warn('[Callix Dialer] ⚠️ Erro ao buscar config do serviço:', configResponse.status, errorText);
        }
      } catch (configErr) {
        console.error('[Callix Dialer] ❌ Erro ao buscar config do serviço:', configErr);
      }

      // Salvar sessão no localStorage
      if (typeof window !== 'undefined') {
        try {
          const STORAGE_KEYS = getStorageKeys(user);
          localStorage.setItem(LAST_USERNAME_KEY, user);
          localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, data.userSessionToken);
          localStorage.setItem(STORAGE_KEYS.DOMAIN, data.domain || 'villelabrasil.callix.com.br');
          localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString());
          console.log('[Callix Dialer] Sessão salva no localStorage');
        } catch (err) {
          console.warn('[Callix Dialer] Não foi possível salvar sessão:', err);
        }
      }

      console.log('[Callix Dialer] Autenticado com sucesso:', user);
    } catch (err) {
      console.error('[Callix Dialer] Erro ao autenticar:', err);
      setError(err instanceof Error ? err.message : 'Erro ao autenticar');
      setIsAuthenticated(false);
      setSessionToken(null);
    } finally {
      setIsLoading(false);
    }
  }, [options.apiUrl, isAuthenticated, sessionToken]);

  // Auto-login quando há presetUsername
  // Inclui cleanup de sessões SIP no servidor ANTES de fazer login
  useEffect(() => {
    if (!presetUsername) {
      lastPresetRef.current = null;
      autoLoginAttemptedRef.current = false;
      return;
    }

    if (lastPresetRef.current !== presetUsername) {
      lastPresetRef.current = presetUsername;
      autoLoginAttemptedRef.current = false;
    }

    if (isAuthenticated || isLoading || isRestoringSession || autoLoginAttemptedRef.current) {
      return;
    }

    autoLoginAttemptedRef.current = true;
    void handleLogin(presetUsername);
  }, [presetUsername, isAuthenticated, isLoading, isRestoringSession, handleLogin]);

  // Auto-login quando username é restaurado do localStorage (sem presetUsername)
  // Inclui cleanup de sessões SIP no servidor ANTES de fazer login
  useEffect(() => {
    // Só faz auto-login se:
    // 1. Não há presetUsername (senão o outro useEffect cuida)
    // 2. Há username restaurado do localStorage
    // 3. Não está autenticado ainda
    // 4. Não está carregando
    // 5. Terminou de restaurar sessão
    // 6. Ainda não tentou auto-login
    if (presetUsername || !username || isAuthenticated || isLoading || isRestoringSession || autoLoginAttemptedRef.current) {
      return;
    }

    console.log('[Callix Dialer] Auto-login com username restaurado:', username);
    autoLoginAttemptedRef.current = true;
    void handleLogin(username);
  }, [presetUsername, username, isAuthenticated, isLoading, isRestoringSession, handleLogin]);

  const handleLogout = useCallback(async () => {
    const currentUsername = username;
    
    // PASSO 1: Chamar goOffline() ANTES de desmontar o CallixClientProvider
    // Isso envia SIP UNREGISTER para o servidor Kamailio
    if (cleanupRef.current) {
      console.log('[Callix Dialer] 📴 Logout: Chamando goOffline()...');
      cleanupRef.current.goOffline();
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('[Callix Dialer] 🛑 Logout: Chamando stop()...');
      cleanupRef.current.stop();
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setUsername(presetUsername ?? null);
    setIsAuthenticated(false);
    setSessionToken(null);
    setCallixDomain(null);
    setError(null);
    autoLoginAttemptedRef.current = false;
    lastPresetRef.current = presetUsername ?? null;

    // Limpar localStorage
    if (typeof window !== 'undefined' && currentUsername) {
      try {
        const STORAGE_KEYS = getStorageKeys(currentUsername);
        localStorage.removeItem(LAST_USERNAME_KEY);
        localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.DOMAIN);
        localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
        console.log('[Callix Dialer] Sessão removida do localStorage');
      } catch (err) {
        console.warn('[Callix Dialer] Não foi possível limpar sessão:', err);
      }
    }
  }, [username, presetUsername]);

  // Determinar qual componente usar baseado no mode OU uiVariant
  // Se uiVariant='mobile', usar InlineDialer para redirecionar ao MobileDialer
  const DialerComponent = (options.mode === 'inline' || uiVariant === 'mobile') ? InlineDialer : FloatingDialer;
  const dialerMode = options.mode; // Passa mode para o InlineDialer decidir sobre modais

  // Se está desconectando, mostrar loading enquanto aguarda a conexão SIP ser fechada
  if (isDisconnecting) {
    return (
      <DialerConfigContext.Provider value={{ skipManualQualification }}>
        <DialerComponent
          isAuthenticated={false}
          username={username}
          onLogin={handleLogin}
          onLogout={handleLogout}
          isLoadingSession={true}
          sessionError={null}
          variant={uiVariant}
          portalTarget={portalTarget}
          mode={dialerMode}
        />
      </DialerConfigContext.Provider>
    );
  }

  // Se não autenticado, mostrar tela de login
  if (!isAuthenticated || !sessionToken || !callixDomain) {
    return (
      <DialerConfigContext.Provider value={{ skipManualQualification }}>
        <DialerComponent
          isAuthenticated={false}
          username={username}
          onLogin={handleLogin}
          onLogout={handleLogout}
          isLoadingSession={isLoading || isRestoringSession}
          sessionError={error}
          variant={uiVariant}
          portalTarget={portalTarget}
          mode={dialerMode}
        />
      </DialerConfigContext.Provider>
    );
  }

  // Autenticado - renderiza com CallixClientProvider
  // A key única garante que o componente seja completamente recriado a cada login
  // O SdkCleanupManager permite chamar goOffline/stop antes do unmount
  return (
    <DialerConfigContext.Provider value={{ skipManualQualification }}>
      <CallixClientProvider
        key={providerKey}
        domain={callixDomain}
        userSessionToken={sessionToken}
      >
        <SdkCleanupManager ref={cleanupRef}>
          <DialerComponent
            isAuthenticated={isAuthenticated}
            username={username}
            onLogin={handleLogin}
            onLogout={handleLogout}
            isLoadingSession={isLoading}
            sessionError={error}
            variant={uiVariant}
            portalTarget={portalTarget}
            mode={dialerMode}
          />
        </SdkCleanupManager>
      </CallixClientProvider>
    </DialerConfigContext.Provider>
  );
}

// Função de conveniência para inicialização rápida
// Similar a: quick_init() no Python
function init(options: CallixDialerOptions): CallixDialerWidget {
  console.log('[Callix Dialer] 🔧 Widget v1.7.0 inicializando...');
  const widget = new CallixDialerWidget(options);
  widget.init();
  return widget;
}

// Objeto principal exportado - funciona tanto como named export quanto via window
const CallixDialer = {
  CallixDialerWidget,
  init,
  version: '1.7.0',
};

// Named exports para ES modules
export { CallixDialerWidget, init };

// Default export para compatibilidade
export default CallixDialer;

// Tornar disponível globalmente quando carregado via <script>
// Isso garante que window.CallixDialer.init() funcione no browser
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).CallixDialer = CallixDialer;
}
